import { buildApiUrl } from "../utils/apiConfig";
import { getToken } from "../utils/auth";

export interface DeleteAccountResponse {
    success: boolean;
    error?: string;
}

/**
 * Request account deletion for the signed-in administrator.
 * Last administrator (account holder): deactivates every remaining organisation user immediately,
 * retains data for up to 90 days plus 30 days for reactivation, then permanently deletes the company.
 * When other administrators remain: reassigns staff/athletes and blocks only this admin.
 */
export async function requestAccountDeletion(userId: string): Promise<DeleteAccountResponse> {
    try {
        const response = await fetch(buildApiUrl(`api/admin-profile`), {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include' // Send HTTP-only cookies
        });

        if (response.status === 204) {
            // Success - No Content
            return { success: true };
        }

        if (response.status === 400) {
            return {
                success: false,
                error: "Account deletion already requested"
            };
        }

        if (response.status === 403) {
            return {
                success: false,
                error: "You are not authorized to delete this account"
            };
        }

        if (response.status === 404) {
            return {
                success: false,
                error: "Account not found"
            };
        }

        // For other error responses, try to parse error message
        try {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || `Request failed with status ${response.status}`
            };
        } catch {
            return {
                success: false,
                error: `Request failed with status ${response.status}`
            };
        }

    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : "Network error occurred"
        };
    }
}

